import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { pb } from '../services/pocketbase';
import { useAuth } from './AuthContext';
import { resolveCatalogSku, cleanSku, getBaseSku, isModeloComMedida } from '../utils/skuUtils';
import { avaliarDisponibilidadeMolde } from '../utils/moldRingUtils';
import { taxonomySeedService } from '../services/taxonomySeedService';
import { extractSubestoqueCera } from '../services/produtoDadosService';
import { catalogStorageService } from '../services/catalogStorageService';

// Helpers para extração de tags e propriedades do catálogo na carga inicial
const extractStoneColor = (record) => {
    const tagsSource = record.full_data?.tags;
    if (tagsSource) {
        try {
            let tagsArray = typeof tagsSource === "string"
                ? JSON.parse(tagsSource.replace(/\\"/g, '"'))
                : tagsSource;
            
            if (Array.isArray(tagsArray)) {
                const stoneColorTag = tagsArray.find(t => 
                    t.group === "Cor da Pedra" || 
                    t.group === "Cor da pedra" || 
                    t.group === "Cor" || 
                    t.group === "cor" ||
                    t.group === "COR"
                );
                if (stoneColorTag && stoneColorTag.value && typeof stoneColorTag.value === "string" && stoneColorTag.value.trim() !== "") {
                    return stoneColorTag.value.trim();
                }
            }
        } catch (e) {}
    }
    const data = record.full_data || record;
    const fields = ["Cor da Pedra", "Cor", "cor", "stoneColor", "stone_color", "Cor da pedra", "COR"];
    for (const field of fields) {
        if (data[field] && typeof data[field] === "string" && data[field].trim() !== "") {
            return data[field].trim();
        }
    }
    return "Não Definido";
};

const extractCategory = (record) => {
    const tagsSource = record.full_data?.tags;
    if (tagsSource) {
        try {
            let tagsArray = typeof tagsSource === "string"
                ? JSON.parse(tagsSource.replace(/\\"/g, '"'))
                : tagsSource;
            
            if (Array.isArray(tagsArray)) {
                const categoryTag = tagsArray.find(t => 
                    t.group === "Categoria" || 
                    t.group === "categoria" || 
                    t.group === "Category" || 
                    t.group === "category" ||
                    t.group === "CATEGORIA"
                );
                if (categoryTag && categoryTag.value && typeof categoryTag.value === "string" && categoryTag.value.trim() !== "") {
                    return categoryTag.value.trim();
                }
            }
        } catch (e) {}
    }
    const data = record.full_data || record;
    const fields = ["Categoria", "categoria", "category", "Category", "CATEGORIA"];
    for (const field of fields) {
        if (data[field] && typeof data[field] === "string" && data[field].trim() !== "") {
            return data[field].trim();
        }
    }
    return "Não Definido";
};

/**
 * Constrói o objeto canônico do produto a partir do registro do PocketBase e mapas de molde
 */
const buildCatalogItem = (record, moldesMap = {}) => {
    const candidateKeys = new Set();

    const addKey = (val) => {
        if (val) {
            const clean = String(val).trim().toUpperCase();
            if (clean) candidateKeys.add(clean);
        }
    };

    addKey(record.sku);
    addKey(record.name);
    addKey(record.full_data?.sku);
    addKey(record.full_data?.SKU);
    addKey(record.full_data?.model);
    addKey(record.full_data?.Model);
    addKey(record.full_data?.modelo);
    addKey(record.full_data?.slug);

    if (candidateKeys.size === 0) return null;

    const rawImage = record.image_url || record.full_data?.image_default || "";
    const isetId = record.full_data?.id;
    let finalImageUrl = null;

    if (rawImage) {
        if (String(rawImage).startsWith("http")) {
            finalImageUrl = rawImage;
        } else if (isetId) {
            finalImageUrl = `https://cdn.iset.io/assets/34692/produtos/${isetId}/${rawImage}`;
        } else {
            finalImageUrl = rawImage;
        }
    }

    let rawTags = record.full_data?.tags || "";
    if (Array.isArray(rawTags)) rawTags = rawTags.join(", ");

    const isInactive = record.full_data?.status === 'inactive' || record.status === 'inactive' || record.full_data?.active === false;

    const cleanItemSku = record.sku || record.full_data?.sku || Array.from(candidateKeys)[0];
    const cleanPai = getBaseSku(cleanSku(cleanItemSku));

    const hasMolde = Boolean(
        record.full_data?.tem_molde ||
        record.full_data?.processo_fabricacao === "borracha" ||
        moldesMap[cleanPai] ||
        moldesMap[cleanSku(cleanItemSku)]
    );

    const itemObj = {
        _pbId: record.id,
        sku: cleanItemSku,
        ...record.full_data,
        name: record.name || record.full_data?.name || record.full_data?.nome || record.full_data?.title || "Sem Nome",
        image: finalImageUrl,
        image_default: finalImageUrl,
        image_url: finalImageUrl,
        price: Number(record.full_data?.price || 0),
        tags: rawTags,
        model: record.full_data?.model || record.full_data?.Model || record.full_data?.modelo || record.full_data?.Modelo || "",
        category: extractCategory(record),
        stoneColor: extractStoneColor(record),
        isInactive: isInactive,
        status: record.full_data?.status || record.status || "active",
        tem_molde: hasMolde
    };

    return { candidateKeys: Array.from(candidateKeys), itemObj };
};

/**
 * Constrói o mapa de indexação completo em memória
 */
const buildCatalogMap = (records, moldesMap = {}) => {
    const map = {};
    (records || []).forEach(record => {
        const built = buildCatalogItem(record, moldesMap);
        if (built) {
            const { candidateKeys, itemObj } = built;
            candidateKeys.forEach(k => {
                map[k] = itemObj;
                // Multi-indexação pelo SKU Base (sem aro e sem -Z)
                const baseK = resolveCatalogSku ? k.split("-").slice(0, 2).join("-") : k;
                if (baseK && !map[baseK]) {
                    map[baseK] = itemObj;
                }
            });
        }
    });
    return map;
};

// Criação do Contexto
const CatalogContext = createContext({});

export function CatalogProvider({ children }) {
    const { user } = useAuth();
    const [catalogMap, setCatalogMap] = useState({});
    const [moldeSkuMap, setMoldeSkuMap] = useState({});
    const [subestoqueCeraMap, setSubestoqueCeraMap] = useState({});
    const [loading, setLoading] = useState(true);

    // Referências em memória para agilizar operações Delta
    const rawRecordsRef = useRef(new Map());
    const lastSyncTimeRef = useRef(null);
    const isSyncingRef = useRef(false);

    const loadCatalog = useCallback(async (isManualRefresh = false) => {
        if (!user) {
            setCatalogMap({});
            setMoldeSkuMap({});
            setSubestoqueCeraMap({});
            setLoading(true);
            rawRecordsRef.current.clear();
            lastSyncTimeRef.current = null;
            return;
        }

        if (isSyncingRef.current) return;
        isSyncingRef.current = true;

        try {
            // Se for refresh manual (botão do usuário), limpa o cache local
            if (isManualRefresh) {
                console.log("🔄 CatalogProvider: Atualização manual forçada. Limpando cache local...");
                await catalogStorageService.clearCache();
                rawRecordsRef.current.clear();
                lastSyncTimeRef.current = null;
                setLoading(true);
            }

            // -------------------------------------------------------------
            // ETAPA 1: Carregamento Ultra-rápido via IndexedDB (< 50ms)
            // -------------------------------------------------------------
            let hadCachedData = false;
            if (!isManualRefresh) {
                try {
                    const cached = await catalogStorageService.getCachedCatalogData();
                    if (cached && cached.records && cached.records.length > 0) {
                        hadCachedData = true;
                        console.log(`⚡ CatalogProvider: Restaurado do IndexedDB com ${cached.count} itens em < 50ms!`);

                        rawRecordsRef.current.clear();
                        cached.records.forEach(r => rawRecordsRef.current.set(r.id, r));
                        lastSyncTimeRef.current = cached.lastSyncTime;

                        const moldes = cached.moldesMap || {};
                        const cera = cached.estoqueMap || {};
                        setMoldeSkuMap(moldes);
                        setSubestoqueCeraMap(cera);

                        const initialMap = buildCatalogMap(cached.records, moldes);
                        setCatalogMap(initialMap);

                        // Libera a aplicação imediatamente para uso!
                        setLoading(false);
                    }
                } catch (cacheErr) {
                    console.warn("[CatalogProvider] Aviso ao ler cache IndexedDB:", cacheErr);
                }
            }

            // Se não tínhamos dados em cache, precisa exibir o loading na tela
            if (!hadCachedData) {
                setLoading(true);
            }

            // -------------------------------------------------------------
            // ETAPA 2: Sincronização em Background (Delta-Sync ou Full Sync)
            // -------------------------------------------------------------
            const fieldsToFetch = ['id', 'sku', 'name', 'image_url', 'full_data', 'updated'];
            const lastSync = lastSyncTimeRef.current;

            if (hadCachedData && lastSync && !isManualRefresh) {
                // --- DELTA SYNC INCREMENTAL ---
                console.log(`🔄 CatalogProvider: Verificando delta sync (modificados após ${lastSync})...`);

                const [deltaRecords, pdRecords] = await Promise.all([
                    pb.collection('catalog').getFullList({
                        filter: `updated > "${lastSync.replace('T', ' ')}"`,
                        fields: fieldsToFetch.join(',')
                    }).catch(err => {
                        console.warn("Aviso ao buscar delta do catálogo:", err?.message);
                        return [];
                    }),
                    pb.collection('produto_dados').getFullList({
                        filter: 'tem_molde = true || ficha_tecnica.tem_molde = true || ficha_tecnica.tipo_processo = "borracha"',
                        fields: 'id,sku_pai,tem_molde,ficha_tecnica'
                    }).catch(() => {
                        return pb.collection('produto_dados').getFullList({
                            fields: 'id,sku_pai,tem_molde,ficha_tecnica'
                        }).catch(() => []);
                    })
                ]);

                // Atualiza mapa de moldes e estoques de cera
                const currentMoldes = {};
                const currentEstoque = {};
                (pdRecords || []).forEach(pd => {
                    const cleanPai = getBaseSku(cleanSku(pd.sku_pai));
                    if (cleanPai) {
                        const has = Boolean(
                            pd.tem_molde || 
                            pd.ficha_tecnica?.tem_molde || 
                            pd.ficha_tecnica?.tipo_processo === "borracha"
                        );
                        if (has) {
                            currentMoldes[cleanPai] = true;
                            currentEstoque[cleanPai] = extractSubestoqueCera(pd);
                        }
                    }
                });
                setMoldeSkuMap(currentMoldes);
                setSubestoqueCeraMap(currentEstoque);
                catalogStorageService.saveMoldesAndCera(currentMoldes, currentEstoque);

                const currentSyncTime = new Date().toISOString();
                lastSyncTimeRef.current = currentSyncTime;

                if (deltaRecords && deltaRecords.length > 0) {
                    console.log(`✨ CatalogProvider: ${deltaRecords.length} produtos atualizados via delta sync.`);
                    deltaRecords.forEach(r => rawRecordsRef.current.set(r.id, r));

                    await catalogStorageService.applyDeltaUpdates(deltaRecords, [], currentSyncTime);

                    const allRecords = Array.from(rawRecordsRef.current.values());
                    const updatedMap = buildCatalogMap(allRecords, currentMoldes);
                    setCatalogMap(updatedMap);
                } else {
                    console.log("✅ CatalogProvider: Catálogo 100% atualizado. Nenhum dado transferido.");
                    await catalogStorageService.applyDeltaUpdates([], [], currentSyncTime);
                }

            } else {
                // --- FULL SYNC INICIAL (Quando não há cache local ou refresh forçado) ---
                console.log("🏗️ CatalogProvider: Iniciando carga completa do catálogo...");

                const [records, pdRecords] = await Promise.all([
                    pb.collection('catalog').getFullList({
                        sort: 'sku',
                        fields: fieldsToFetch.join(',')
                    }),
                    pb.collection('produto_dados').getFullList({
                        filter: 'tem_molde = true || ficha_tecnica.tem_molde = true || ficha_tecnica.tipo_processo = "borracha"',
                        fields: 'id,sku_pai,tem_molde,ficha_tecnica'
                    }).catch(() => {
                        return pb.collection('produto_dados').getFullList({
                            fields: 'id,sku_pai,tem_molde,ficha_tecnica'
                        }).catch(() => []);
                    })
                ]);

                const moldesMap = {};
                const estoqueMap = {};
                (pdRecords || []).forEach(pd => {
                    const cleanPai = getBaseSku(cleanSku(pd.sku_pai));
                    if (cleanPai) {
                        const has = Boolean(
                            pd.tem_molde || 
                            pd.ficha_tecnica?.tem_molde || 
                            pd.ficha_tecnica?.tipo_processo === "borracha"
                        );
                        if (has) {
                            moldesMap[cleanPai] = true;
                            estoqueMap[cleanPai] = extractSubestoqueCera(pd);
                        }
                    }
                });
                setMoldeSkuMap(moldesMap);
                setSubestoqueCeraMap(estoqueMap);

                rawRecordsRef.current.clear();
                records.forEach(r => rawRecordsRef.current.set(r.id, r));

                const currentSyncTime = new Date().toISOString();
                lastSyncTimeRef.current = currentSyncTime;

                // Salva no IndexedDB
                catalogStorageService.saveFullCatalog(records, moldesMap, estoqueMap, currentSyncTime);

                const fullMap = buildCatalogMap(records, moldesMap);
                setCatalogMap(fullMap);
                console.log(`✅ CatalogProvider: Carga completa finalizada. ${records.length} produtos em memória. Moldes ativos: ${Object.keys(moldesMap).length}`);
            }

        } catch (error) {
            console.error("❌ CatalogProvider Erro na carga:", error);
        } finally {
            setLoading(false);
            isSyncingRef.current = false;
        }
    }, [user]);

    useEffect(() => {
        loadCatalog(false);
        if (user) {
            taxonomySeedService.seedTaxonomy().catch(err => {
                console.warn("⚠️ Seed de taxonomia:", err?.message || err);
            });
        }
    }, [user, loadCatalog]);

    // Listener para atualizações em tempo real de Molde/Borracha e Subestoque de Cera
    useEffect(() => {
        const handleMoldeUpdated = (e) => {
            const { skuPai, temMolde } = e.detail || {};
            if (!skuPai) return;
            const cleanPai = getBaseSku(cleanSku(skuPai));

            setMoldeSkuMap(prev => ({
                ...prev,
                [cleanPai]: !!temMolde
            }));

            setCatalogMap(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(k => {
                    if (getBaseSku(cleanSku(k)) === cleanPai) {
                        next[k] = { ...next[k], tem_molde: !!temMolde };
                    }
                });
                return next;
            });
        };

        const handleCeraUpdated = (e) => {
            const { skuPai, subestoque } = e.detail || {};
            if (!skuPai || !subestoque) return;
            const cleanPai = getBaseSku(cleanSku(skuPai));

            setSubestoqueCeraMap(prev => ({
                ...prev,
                [cleanPai]: subestoque
            }));
        };

        const handlePrecoUpdated = (e) => {
            const { skuPai, precoVendaBRL } = e.detail || {};
            if (!skuPai || typeof precoVendaBRL !== "number" || isNaN(precoVendaBRL)) return;
            const cleanPai = getBaseSku(cleanSku(skuPai));

            setCatalogMap(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(k => {
                    if (getBaseSku(cleanSku(k)) === cleanPai) {
                        next[k] = {
                            ...next[k],
                            price: precoVendaBRL,
                            full_data: {
                                ...(next[k].full_data || {}),
                                price: precoVendaBRL
                            }
                        };
                    }
                });
                return next;
            });
        };

        window.addEventListener("produto-dados-molde-updated", handleMoldeUpdated);
        window.addEventListener("produto-dados-cera-updated", handleCeraUpdated);
        window.addEventListener("produto-dados-preco-updated", handlePrecoUpdated);

        // Subscrição Realtime PocketBase para produto_dados
        let unsubPd = null;
        pb.collection("produto_dados").subscribe("*", (e) => {
            if (e.record?.sku_pai) {
                const cleanPai = getBaseSku(cleanSku(e.record.sku_pai));
                const has = Boolean(
                    e.record.tem_molde || 
                    e.record.ficha_tecnica?.tem_molde || 
                    e.record.ficha_tecnica?.tipo_processo === "borracha"
                );

                setMoldeSkuMap(prev => ({ ...prev, [cleanPai]: has }));
                if (e.record.ficha_tecnica?.subestoque_cera) {
                    setSubestoqueCeraMap(prev => ({
                        ...prev,
                        [cleanPai]: extractSubestoqueCera(e.record)
                    }));
                }

                setCatalogMap(prev => {
                    const next = { ...prev };
                    Object.keys(next).forEach(k => {
                        if (getBaseSku(cleanSku(k)) === cleanPai) {
                            next[k] = { ...next[k], tem_molde: has };
                        }
                    });
                    return next;
                });
            }
        }).then(u => { unsubPd = u; }).catch(() => {});

        // Subscrição Realtime PocketBase para catalog
        let unsubCat = null;
        pb.collection("catalog").subscribe("*", (e) => {
            if (e.record) {
                if (e.action === "create" || e.action === "update") {
                    rawRecordsRef.current.set(e.record.id, e.record);
                    catalogStorageService.applyDeltaUpdates([e.record], [], new Date().toISOString());

                    const built = buildCatalogItem(e.record, moldeSkuMap);
                    if (built) {
                        setCatalogMap(prev => {
                            const next = { ...prev };
                            built.candidateKeys.forEach(k => {
                                next[k] = built.itemObj;
                                const baseK = resolveCatalogSku ? k.split("-").slice(0, 2).join("-") : k;
                                if (baseK && !next[baseK]) {
                                    next[baseK] = built.itemObj;
                                }
                            });
                            return next;
                        });
                    }
                } else if (e.action === "delete") {
                    rawRecordsRef.current.delete(e.record.id);
                    catalogStorageService.applyDeltaUpdates([], [e.record.id], new Date().toISOString());

                    setCatalogMap(prev => {
                        const next = { ...prev };
                        Object.keys(next).forEach(k => {
                            if (next[k]?._pbId === e.record.id) {
                                delete next[k];
                            }
                        });
                        return next;
                    });
                }
            }
        }).then(u => { unsubCat = u; }).catch(() => {});

        return () => {
            window.removeEventListener("produto-dados-molde-updated", handleMoldeUpdated);
            window.removeEventListener("produto-dados-cera-updated", handleCeraUpdated);
            window.removeEventListener("produto-dados-preco-updated", handlePrecoUpdated);
            if (unsubPd) unsubPd();
            if (unsubCat) unsubCat();
        };
    }, [moldeSkuMap]);

    // Função de busca Inteligente Canônica (resolveCatalogSku)
    const findItem = useCallback((sku) => {
        if (!sku) return null;
        const resolved = resolveCatalogSku(sku, catalogMap);
        const cleanPai = getBaseSku(cleanSku(sku));
        const hasMolde = Boolean(moldeSkuMap[cleanPai] || moldeSkuMap[cleanSku(sku)] || resolved?.tem_molde);
        
        if (resolved) {
            return {
                ...resolved,
                tem_molde: hasMolde
            };
        }
        if (hasMolde) {
            return {
                sku,
                tem_molde: true
            };
        }
        return null;
    }, [catalogMap, moldeSkuMap]);

    const isMoldeSku = useCallback((sku, options = {}) => {
        if (!sku) return false;
        const clean = cleanSku(sku);
        const cleanPai = getBaseSku(clean);
        const hasBaseMolde = Boolean(moldeSkuMap[cleanPai] || moldeSkuMap[clean]);
        const item = catalogMap[clean] || catalogMap[cleanPai];
        const hasCatalogMolde = !!(item?.tem_molde || item?.full_data?.tem_molde || item?.full_data?.processo_fabricacao === "borracha");

        if (!hasBaseMolde && !hasCatalogMolde) return false;

        // Se options contiver order ou se o SKU tiver variação de aro e o item for anel,
        // avalia a disponibilidade contra a matriz de aros físicos da fábrica
        const moldConfig = item?.full_data?.config_borracha || item?.config_borracha || null;
        if (moldConfig && isModeloComMedida(moldConfig.tipo_modelo || item?.tipo_modelo)) {
            const orderOrSku = options.order || sku;
            const avaliacao = avaliarDisponibilidadeMolde(orderOrSku, moldConfig, { catItem: item });
            return avaliacao.disponivel;
        }

        return true;
    }, [catalogMap, moldeSkuMap]);

    const getSubestoqueCera = useCallback((sku) => {
        if (!sku) return { cera_bruta: 0, cera_precravada: 0, localizacao_gaveta: "", historico: [], estoque_por_aro: {} };
        const clean = cleanSku(sku);
        const cleanPai = getBaseSku(clean);
        return subestoqueCeraMap[cleanPai] || subestoqueCeraMap[clean] || {
            cera_bruta: 0,
            cera_precravada: 0,
            localizacao_gaveta: "",
            historico: [],
            estoque_por_aro: {}
        };
    }, [subestoqueCeraMap]);

    const handleForceRefresh = useCallback(() => {
        return loadCatalog(true);
    }, [loadCatalog]);

    return (
        <CatalogContext.Provider value={{ 
            catalog: catalogMap, 
            moldeSkuMap, 
            subestoqueCeraMap, 
            isMoldeSku, 
            getSubestoqueCera, 
            loading, 
            findItem, 
            findCatalogItem: findItem, 
            refreshCatalog: handleForceRefresh 
        }}>
            {children}
        </CatalogContext.Provider>
    );
}

export const useCatalogContext = () => useContext(CatalogContext);